// Reverting turns through their checkpoints (undoing one, or restoring the files of the turns an
// edited message drops). Several conversations may work in one folder: a revert only puts back
// the files its turns own, and says which of them someone else may have changed before it
// writes anything.

/** A turn to revert, as its message's checkpoint describes it. */
export type CheckpointRevertTurn = {
  before: string;
  after: string;
  /** Files another conversation's turn may have changed too during this one. */
  shared?: readonly string[];
  /** Files another conversation's turn wrote during this one: never put back. */
  foreign?: readonly string[];
};

/**
 * Why putting a file back could destroy work the reverted turns did not do: another
 * conversation's turn ran at the same time and may have changed it (`shared`), something changed
 * it between two of the turns (`changed_between`), or after the last of them (`changed_since`).
 */
export type CheckpointConflictReason = 'shared' | 'changed_between' | 'changed_since';

export type CheckpointConflict = { path: string; reason: CheckpointConflictReason };

/**
 * What the user decided for conflicting files: put them back anyway (`overwrite`) or leave them
 * as they are (`keep`). A conflict neither lists refuses the whole revert.
 */
export type CheckpointRevertResolution = {
  overwrite?: readonly string[];
  keep?: readonly string[];
};

export type CheckpointRevertResult = {
  /** Whether the revert took place; never when a conflict is unresolved, or on a dry run. */
  applied: boolean;
  /** The files put back (or that would be), without the kept ones. */
  files: string[];
  /** Every conflict found, resolved or not. */
  conflicts: CheckpointConflict[];
};

/** The conflicts a resolution leaves undecided: they refuse the revert. */
export function unresolvedCheckpointConflicts(
  conflicts: readonly CheckpointConflict[],
  resolution: CheckpointRevertResolution = {},
): CheckpointConflict[] {
  const decided = new Set([...(resolution.overwrite ?? []), ...(resolution.keep ?? [])]);
  return conflicts.filter((conflict) => !decided.has(conflict.path));
}

const REASON_TEXT: Record<CheckpointConflictReason, string> = {
  shared: 'Another conversation was working in this folder and may have changed it too',
  changed_between: 'Changed by something else between these turns',
  changed_since: 'Changed since (by you or another conversation)',
};

/** Why a conflicting file is listed, in a sentence for the user. */
export function checkpointConflictText(reason: CheckpointConflictReason): string {
  return REASON_TEXT[reason];
}
